import { BaseApiService } from './base-api.service';
import { ENDPOINTS } from '../endpoint';
import {
  Media,
  MediaQueryInput,
  UpdateMediaInput,
  MediaUploadInput,
  MediaListResponse,
} from '@repo/types';
import { axiosInstance } from '../axios-instance';
import { AxiosInstance } from 'axios';

export class MediaService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.MEDIA.BASE, axios);
  }
  async upload(file: File, uploadInput?: MediaUploadInput): Promise<Media> {
    const formData = new FormData();
    formData.append('file', file);

    if (uploadInput) {
      Object.entries(uploadInput).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
    }

    const response = await this.axios.post(`${this.endpoint}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadMultiple(files: File[], uploadInput?: MediaUploadInput): Promise<Media[]> {
    const results = await Promise.all(
      files.map(file => this.upload(file, uploadInput))
    );
    return results;
  }

  async getAll(query: MediaQueryInput): Promise<MediaListResponse> {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.axios.get(`${this.endpoint}?${params.toString()}`);
    return response.data;
  }

  async getById(id: string): Promise<Media> {
    const response = await this.axios.get(`${this.endpoint}/${id}`);
    return response.data;
  }

  async update(id: string, updateInput: UpdateMediaInput): Promise<Media> {
    const response = await this.axios.put(`${this.endpoint}/${id}`, updateInput);
    return response.data;
  }

  async delete(id: string): Promise<any> {
    await this.axios.delete(`${this.endpoint}/${id}`);
  }

  async bulkDelete(ids: string[]): Promise<{ successful: number; failed: number }> {
    const response = await this.axios.delete(`${this.endpoint}/bulk`, {
      data: { ids },
    });
    return response.data;
  }

  async bulkUpdate(
    ids: string[],
    updates: UpdateMediaInput
  ): Promise<{ successful: number; failed: number }> {
    const response = await this.axios.put(`${this.endpoint}/bulk/update`, {
      ids,
      updates,
    });
    return response.data;
  }

  async getStats(userId?: string): Promise<{
    totalFiles: number;
    totalSize: number;
    byType: Record<string, number>;
    byFolder: Record<string, number>;
  }> {
    const params = userId ? `?userId=${userId}` : '';
    const response = await this.axios.get(`${this.endpoint}/stats${params}`);
    return response.data;
  }

  async getFolders(): Promise<string[]> {
    const response = await this.axios.get(`${this.endpoint}/folders`);
    return response.data;
  }

  // Helper methods
  getFileTypeIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('sheet')) return '📊';
    return '📁';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  isVideoFile(mimeType: string): boolean {
    return mimeType.startsWith('video/');
  }

  isDocumentFile(mimeType: string): boolean {
    return mimeType.includes('pdf') ||
      mimeType.includes('word') ||
      mimeType.includes('excel') ||
      mimeType.includes('sheet');
  }
}

export const mediaService = new MediaService();
